/**
 * The receipt vouchers list, or the payment vouchers list - same columns,
 * opposite direction.
 */

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";
import FilterBar, { filterRows } from "components/FilterBar";
import {
  FilterSelect,
  FilterDates,
  optionsFromRows,
  matches,
  inDateRange,
} from "components/FilterBar/controls";
import Pagination, { usePagination } from "components/Pagination";
import RowActions from "components/RowActions";

import VoucherDialog from "./VoucherDialog";
import { printVoucher } from "./print";
import { api, problemOf, money, docNo, byCurrency, currencyLine, LOOKUPS } from "./api";

const cell = { fontSize: 14, whiteSpace: "nowrap" };

function VouchersTab({ type, lookups, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [box, setBox] = useState("");
  const [category, setCategory] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  // {voucher} for edit, {} for new, null when closed
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api.get("/vouchers", { type });
      setRows(
        (Array.isArray(data) ? data : []).map((v) => ({
          ...v,
          no: docNo(v.type, v.number),
          // what it is for: its category, or the chart account it is against
          category: v.category || (v.account_code ? `${v.account_code} ${v.account_name}` : ""),
        }))
      );
    } catch (e) {
      setError(problemOf(e, "Could not load vouchers"));
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [type]);

  const saved = () => {
    load();
    onChanged();
  };

  const remove = async (v) => {
    const what = `${v.no} - ${money(v.amount)} ${v.currency}`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${what}? The box balance goes back.`)) return;
    try {
      await api.del(`/vouchers/${v.id}`);
      saved();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(problemOf(e, "Could not delete"));
    }
  };

  const narrowed = rows.filter(
    (r) => inDateRange(r.date, from, to) && matches(r.box, box) && matches(r.category, category)
  );
  const filtered = filterRows(narrowed, query);
  const pager = usePagination(filtered);
  const receipt = type === "receipt";

  return (
    <>
      <FilterBar
        title={
          type === "receipt" ? "Receipt Vouchers · سندات القبض" : "Payment Vouchers · سندات الصرف"
        }
        value={query}
        onChange={setQuery}
        count={filtered.length}
        total={rows.length}
        placeholder="Search number, name, reference..."
        actions={
          <>
            <SuiTypography variant="caption" sx={{ opacity: 0.75, whiteSpace: "nowrap" }}>
              {`${receipt ? "Received" : "Paid out"} ${currencyLine(
                byCurrency(filtered, (r) => r.amount)
              )}`}
            </SuiTypography>
            <Button variant="contained" color="primary" onClick={() => setEditing({})}>
              {receipt ? "New Receipt" : "New Payment"}
            </Button>
          </>
        }
      >
        <FilterSelect
          label="Box"
          icon="account_balance"
          value={box}
          onChange={setBox}
          options={optionsFromRows(rows, "box")}
        />
        <FilterSelect
          label="Category"
          icon="sell"
          value={category}
          onChange={setCategory}
          options={optionsFromRows(rows, "category")}
        />
        <FilterDates label="Date" from={from} to={to} onFrom={setFrom} onTo={setTo} />
      </FilterBar>

      {error && (
        <SuiBox px={3} pb={2}>
          <SuiTypography variant="button" color="error">
            {error}
          </SuiTypography>
        </SuiBox>
      )}

      {loading ? (
        <CargoLoader label="Loading vouchers" />
      ) : (
        <>
          <SuiBox sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 1100 }}>
              <TableHead>
                <TableRow>
                  <TableCell align="center">No.</TableCell>
                  <TableCell align="center">Date</TableCell>
                  <TableCell align="center">{receipt ? "Received from" : "Paid to"}</TableCell>
                  <TableCell align="center">{receipt ? "Into" : "From"}</TableCell>
                  <TableCell align="center">Category / Account</TableCell>
                  <TableCell align="center">Invoice</TableCell>
                  <TableCell align="center">Reference</TableCell>
                  <TableCell align="center">Description</TableCell>
                  <TableCell align="center">Amount</TableCell>
                  <TableCell align="center">Operations</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pager.pageRows.map((v) => (
                  <TableRow key={v.id}>
                    <TableCell align="center" sx={{ ...cell, fontWeight: 700 }}>
                      {v.no}
                    </TableCell>
                    <TableCell align="center" sx={cell}>
                      {v.date}
                    </TableCell>
                    <TableCell align="center" sx={cell}>
                      {v.party_name || "-"}
                    </TableCell>
                    <TableCell align="center" sx={cell}>
                      {v.box || "-"}
                    </TableCell>
                    <TableCell align="center" sx={cell}>
                      {v.category || "-"}
                    </TableCell>
                    <TableCell align="center" sx={cell}>
                      {v.invoice_number ? docNo(v.invoice_type, v.invoice_number) : "-"}
                    </TableCell>
                    <TableCell align="center" sx={cell}>
                      {[v.method, v.reference].filter(Boolean).join(" · ") || "-"}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontSize: 13, maxWidth: 240, whiteSpace: "normal" }}
                    >
                      {v.description || "-"}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={({ palette }) => ({
                        ...cell,
                        fontWeight: 700,
                        color: receipt ? palette.success.main : palette.error.main,
                      })}
                    >
                      {`${money(v.amount)} ${v.currency}`}
                    </TableCell>
                    <TableCell align="center" sx={cell}>
                      <RowActions
                        actions={[
                          {
                            icon: "print",
                            label: "Print",
                            need: "print",
                            tone: "info",
                            onClick: () => printVoucher(v),
                          },
                          {
                            icon: "edit",
                            label: "Edit",
                            onClick: () => setEditing({ voucher: v }),
                          },
                          {
                            icon: "delete",
                            label: "Delete",
                            tone: "danger",
                            onClick: () => remove(v),
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <SuiTypography variant="button" color="text">
                        No vouchers yet
                      </SuiTypography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </SuiBox>
          <Pagination {...pager} />
        </>
      )}

      <VoucherDialog
        open={Boolean(editing)}
        type={type}
        voucher={editing?.voucher || null}
        lookups={lookups}
        onClose={() => setEditing(null)}
        onSaved={saved}
      />
    </>
  );
}

VouchersTab.propTypes = {
  type: PropTypes.oneOf(["receipt", "payment"]).isRequired,
  lookups: LOOKUPS.isRequired,
  onChanged: PropTypes.func.isRequired,
};

export default VouchersTab;
